import { useState, useCallback } from 'react'
import axios from 'axios'
import { environment } from '../config/environment'
import { ChildResponse } from '../types/child'

const API = axios.create({
  baseURL: environment.apiUrl,
})

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export function useChild(childId?: string) {
  const [child, setChild] = useState<ChildResponse | null>(null)
  const [children, setChildren] = useState<ChildResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchChild = useCallback(async (id: string) => {
    setLoading(true)
    try {
      const response = await API.get(`/children/${id}`)
      setChild(response.data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch child')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchChildren = useCallback(async () => {
    setLoading(true)
    try {
      const response = await API.get('/children')
      console.log('fetchChildren response:', response.data)
      // Ensure we have an array
      const data = Array.isArray(response.data) ? response.data : (response.data?.children || [])
      setChildren(data)
      setError(null)
    } catch (err: any) {
      console.error('fetchChildren error:', err)
      setError(err.response?.data?.error || 'Failed to fetch children')
      // Ensure children defaults to empty array on error
      setChildren([])
    } finally {
      setLoading(false)
    }
  }, [])

  const createChild = useCallback(async (name: string) => {
    setLoading(true)
    try {
      const response = await API.post('/children', { name })
      setChild(response.data)
      setError(null)
      return response.data
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create child')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const performAction = useCallback(async (id: string, action: string) => {
    try {
      const response = await API.post(`/children/${id}/action`, { action })
      setChild(response.data)
      setError(null)
      return response.data
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to perform action')
      throw err
    }
  }, [])

  return {
    child,
    children,
    loading,
    error,
    fetchChild,
    fetchChildren,
    createChild,
    performAction,
  }
}
